<template>
  <v-dialog
    :value="value"
    max-width="1180"
    persistent
    scrollable
    content-class="relationship-run-dialog"
    @input="$emit('input', $event)">
    <v-card class="ds-dialog relationship-run">
      <header class="ds-dialog__header">
        <div class="ds-dialog__header-icon">
          <v-icon color="primary">{{ dialogIcon }}</v-icon>
        </div>
        <div class="ds-dialog__header-text">
          <span ref="dialogTitle" class="ds-dialog__title" tabindex="-1">{{ dialogTitle }}</span>
          <span class="ds-dialog__subtitle">
              {{ relationship && relationship.name }} - {{ dialogSubtitle }}
          </span>
        </div>
        <div
          v-if="currentRun"
          class="run-status-pill mr-2"
          :class="[`run-status-pill--${statusMeta.color}`, { 'run-status-pill--urgent': showHeaderExpiry && remainingSeconds < 300 }]"
          role="status"
          aria-live="polite">
          <span class="run-status-pill__state">
            <v-icon small>{{ statusMeta.icon }}</v-icon>{{ statusMeta.label }}
          </span>
          <span v-if="showHeaderExpiry" class="run-status-pill__timer">
            <v-icon small>mdi-timer-outline</v-icon>{{ remainingLabel }}
          </span>
        </div>
        <v-btn icon :disabled="confirming || cancelling" aria-label="Close run dialog" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </header>

      <v-card-text class="ds-dialog__body relationship-run__body">
        <relationship-refusal-banner v-if="problem && problemCopyResolved" class="mb-5" :copy="problemCopyResolved">
          <v-btn v-if="canRepreview" small outlined color="primary" @click="repreview">
            <v-icon left small>mdi-refresh</v-icon>Build new preview
          </v-btn>
        </relationship-refusal-banner>
        <div v-else-if="problem" class="ds-info-box ds-info-box--error mb-5" role="alert">
          <v-icon color="error">mdi-alert-circle-outline</v-icon>
          <div class="flex-grow-1">
            <strong>Relationship run could not continue</strong>
            <div>{{ problemMessage }}</div>
          </div>
          <v-btn v-if="canRepreview" small text color="primary" @click="repreview">Re-preview</v-btn>
        </div>

        <div v-if="pollingProblem && isExecuting" class="ds-info-box ds-info-box--warning mb-5" role="alert">
          <v-icon color="warning">mdi-cloud-alert-outline</v-icon>
          <div class="flex-grow-1">
            <strong>{{ pollingExhausted ? 'Run status needs to be checked' : 'Run status could not be refreshed' }}</strong>
            <div>{{ pollingExhausted
              ? 'Automatic checking paused after 20 minutes. Check again to see whether the run finished.'
              : 'The run may still be processing. Check its status again.' }}</div>
          </div>
          <v-btn small text color="primary" @click="retryPolling">Check status</v-btn>
        </div>

        <section v-if="historyOnly && !currentRun" class="run-history-loading" aria-labelledby="run-history-loading-title">
          <v-progress-circular v-if="loadingRunItems" indeterminate color="primary" size="28" width="3"></v-progress-circular>
          <v-icon v-else color="warning">mdi-history</v-icon>
          <div>
            <h3 id="run-history-loading-title">{{ loadingRunItems ? 'Loading run details' : 'Run details unavailable' }}</h3>
            <p>{{ loadingRunItems ? 'Loading the first page of row decisions.' : 'Close this view or try opening the run again from history.' }}</p>
          </div>
        </section>

        <section v-else-if="!currentRun" aria-label="Rows to preview">
          <div v-if="reconcile" class="ds-selection-card ds-selection-card--selected">
            <div class="ds-selection-card__icon"><v-icon>mdi-link-variant-plus</v-icon></div>
            <div class="ds-selection-card__content">
              <div class="ds-selection-card__title">Previously unresolved rows</div>
              <div class="ds-selection-card__description">Reconnect rows whose related record is now available. No new records are created.</div>
            </div>
            <v-chip small color="primary" outlined>Reconnect</v-chip>
          </div>

          <div v-if="!reconcile && isFieldSync" class="ds-mode-banner mb-5" role="status">
            <v-icon class="ds-mode-banner__icon">mdi-table-sync</v-icon>
            <div class="ds-mode-banner__content">
              <span class="ds-mode-banner__label">Sync mapped fields</span>
              <span class="ds-mode-banner__value">Compare register values with existing matched or connected records and review only the fields that would change. This action never creates a missing related record.</span>
            </div>
          </div>

          <v-radio-group v-if="!reconcile" v-model="scopeKind" hide-details class="run-scope-options" aria-label="Run scope">
            <label
              class="ds-selection-card"
              :class="{
                'ds-selection-card--selected': scopeKind === 'rows',
                'ds-selection-card--disabled': !selectedRowIds.length,
              }"
              :aria-disabled="String(!selectedRowIds.length)">
              <v-radio value="rows" class="mr-3" :disabled="!selectedRowIds.length"></v-radio>
              <div class="ds-selection-card__icon"><v-icon>mdi-format-list-checks</v-icon></div>
              <div class="ds-selection-card__content">
                <div class="ds-selection-card__title">Selected rows</div>
                <div class="ds-selection-card__description">
                  {{ selectedRowIds.length ? `Only the ${selectedRowIds.length} rows selected in Data` : 'Select rows in Data first' }}
                </div>
              </div>
              <v-chip small outlined>{{ selectedRowIds.length }}</v-chip>
            </label>
            <label
              class="ds-selection-card mt-3"
              :class="{
                'ds-selection-card--selected': scopeKind === 'filtered',
                'ds-selection-card--disabled': !filteredRowsAvailable,
              }"
              :aria-disabled="String(!filteredRowsAvailable)">
              <v-radio value="filtered" class="mr-3" :disabled="!filteredRowsAvailable"></v-radio>
              <div class="ds-selection-card__icon"><v-icon>mdi-filter-variant</v-icon></div>
              <div class="ds-selection-card__content">
                <div class="ds-selection-card__title">Filtered rows</div>
                <div class="ds-selection-card__description">{{ filteredRowsDescription }}</div>
              </div>
              <v-chip v-if="filteredRowsCount !== null" small outlined>{{ filteredRowsCount }}</v-chip>
            </label>
            <label class="ds-selection-card mt-3" :class="{ 'ds-selection-card--selected': scopeKind === 'register' }">
              <v-radio value="register" class="mr-3"></v-radio>
              <div class="ds-selection-card__icon"><v-icon>mdi-table-large</v-icon></div>
              <div class="ds-selection-card__content">
                <div class="ds-selection-card__title">All applicable rows</div>
                <div class="ds-selection-card__description">Includes every row covered by this relationship and updates its overall health.</div>
              </div>
            </label>
          </v-radio-group>

          <div class="ds-info-box mt-5">
            <v-icon color="info">mdi-shield-search-outline</v-icon>
            <div>No related record is changed while the preview is built. The preview is saved for 30 minutes so you can review it safely.</div>
          </div>
        </section>

        <div v-else class="run-content">
          <div v-if="historyOnly" class="ds-mode-banner mb-5" role="status">
            <v-icon class="ds-mode-banner__icon">{{ awaitingConfirmation ? 'mdi-content-save-outline' : 'mdi-lock-outline' }}</v-icon>
            <div class="ds-mode-banner__content">
              <span class="ds-mode-banner__label">{{ awaitingConfirmation ? 'Saved preview' : 'Read-only history' }}</span>
              <span class="ds-mode-banner__value">
                {{ awaitingConfirmation
                  ? 'This preview is still awaiting a decision. Close keeps it available; Cancel preview discards it.'
                  : 'Review the saved decisions and any rows that need attention. Previous runs cannot be changed.' }}
              </span>
            </div>
          </div>

          <div class="run-summary-grid my-5" aria-label="Run decision summary" aria-live="polite">
            <div class="run-summary-card">
              <span>{{ isFieldSync ? 'Will update' : 'Will change' }}</span><strong>{{ actionCount }}</strong>
            </div>
            <div class="run-summary-card">
              <span>No change</span><strong>{{ outcomeCount }}</strong>
            </div>
            <div class="run-summary-card">
              <span>Failed</span><strong>{{ failedCount }}</strong>
            </div>
            <div class="run-summary-card">
              <span>Changed since preview</span><strong>{{ driftedCount }}</strong>
            </div>
          </div>

          <div v-if="isRedacted" class="ds-mode-banner ds-mode-banner--warning mb-5" role="alert">
            <v-icon class="ds-mode-banner__icon">mdi-eye-off-outline</v-icon>
            <div class="ds-mode-banner__content">
              <span class="ds-mode-banner__label">Restricted details</span>
              <span class="ds-mode-banner__value">{{ redactionSummary }}</span>
            </div>
          </div>

          <div v-if="awaitingConfirmation && !hasValidPreviewEnvelope" class="ds-mode-banner ds-mode-banner--warning mb-5" role="alert">
            <v-icon class="ds-mode-banner__icon">mdi-file-alert-outline</v-icon>
            <div class="ds-mode-banner__content">
              <span class="ds-mode-banner__label">Confirmation blocked:</span>
              <span class="ds-mode-banner__value">This preview is incomplete or has no valid expiry. Build a new preview.</span>
            </div>
          </div>

          <div v-if="awaitingConfirmation && confirmationBlockers.length" class="run-contract-blockers mb-5" role="alert">
            <v-icon color="warning">mdi-lock-alert-outline</v-icon>
            <div>
              <strong>Approval is locked until every row is reviewable</strong>
              <ul>
                <li v-for="blocker in confirmationBlockers" :key="blocker">{{ blocker }}</li>
              </ul>
            </div>
          </div>

          <div v-if="isExecuting" class="run-progress mb-5">
            <div class="d-flex align-center justify-space-between mb-2">
              <strong>{{ statusMeta.label }}</strong>
              <span class="text-caption">Processing row decisions</span>
            </div>
            <v-progress-linear
              :indeterminate="!progressPercent"
              :value="progressPercent"
              color="primary"
              height="8"
              rounded
              :aria-label="`${statusMeta.label}: ${Math.round(progressPercent)} percent complete`"></v-progress-linear>
            <p class="text-caption mt-2 mb-0">Rows completed before a later error stay completed. Recovery always starts with a new preview.</p>
          </div>

          <relationship-refusal-strip
            v-if="refusalSummary.total"
            class="mb-4"
            :summary="refusalSummary"
            :active-key="refusalFilter"
            :owner-kind="ownerKind"
            :partial="Boolean(visibleItemsCursor)"
            @filter="refusalFilter = $event" />

          <div class="run-items-header mb-3">
            <div>
              <h3 class="text-subtitle-1 font-weight-medium mb-0">Row decisions</h3>
              <span class="text-caption">Review what will happen to each record. Tag names open in a new tab. Scroll inside the table to keep the approval controls in place.</span>
            </div>
            <div class="run-items-header__actions">
              <v-btn v-if="refusalFilter" small text color="primary" @click="refusalFilter = null">
                <v-icon left small>mdi-filter-remove-outline</v-icon>Show all rows
              </v-btn>
              <v-btn-toggle v-if="isTerminal" v-model="itemView" mandatory dense aria-label="Run item view">
                <v-btn small value="all">All</v-btn>
                <v-btn small value="attention" :loading="loadingAttentionItems">Needs attention</v-btn>
              </v-btn-toggle>
              <v-chip small outlined>{{ visibleItemsLabel }}</v-chip>
            </div>
          </div>

          <v-data-table
            :headers="itemHeaders"
            :items="displayItems"
            :items-per-page="10"
            :loading="loadingRunItems"
            loading-text="Loading row decisions..."
            aria-label="Relationship preview row decisions"
            class="relationship-run__table run-ledger"
            fixed-header
            dense
            item-key="planItemKey"
            :item-class="itemRowClass"
            show-expand
            :single-expand="false"
            :expanded.sync="expandedItems">
            <template v-slot:[`item.data-table-expand`]="{ item, isExpanded, expand }">
              <v-btn
                v-if="rowExpandable(item)"
                icon
                small
                class="run-ledger__chev"
                :class="{ 'run-ledger__chev--open': isExpanded }"
                :aria-expanded="isExpanded ? 'true' : 'false'"
                :aria-label="isExpanded ? 'Collapse field changes' : 'Expand field changes'"
                @click="expand(!isExpanded)">
                <v-icon small>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <template v-slot:[`item.row`]="{ item }">
              <div class="run-row-snapshot py-1">
                <strong>{{ item.recordLabel }}</strong>
                <span :class="{ 'run-value-state--restricted': item.recordContextState !== 'available' }">
                  <v-icon v-if="item.recordContextState === 'redacted'" x-small>mdi-eye-off-outline</v-icon>
                  <v-icon v-else-if="item.recordContextState === 'unavailable'" x-small>mdi-database-off-outline</v-icon>
                  {{ item.recordContext }}
                </span>
              </div>
            </template>
            <template v-slot:[`item.decision`]="{ item }">
              <span v-if="item.refusal" class="run-dec run-dec--warning" :title="item.refusal.title">
                <v-icon small>{{ item.refusal.icon }}</v-icon><span class="run-dec__text">{{ item.refusal.title }}</span>
              </span>
              <span v-else class="run-dec" :class="`run-dec--${decisionColor(item)}`">
                <v-icon small>{{ decisionIcon(item) }}</v-icon>{{ decisionLabel(item) }}
              </span>
            </template>
            <template v-slot:[`item.target`]="{ item }">
              <span v-if="item.redacted" class="text--secondary"><v-icon x-small>mdi-eye-off</v-icon> {{ item.redactionLabel }}</span>
              <relationship-repoint-cell
                v-else-if="item.repoint"
                :targets="item.repoint"
                :source-value="item.repointSourceValue" />
              <div v-else class="run-target-identity">
                <template v-if="targetName(item)">
                  <span>{{ targetName(item) }}</span>
                  <small>{{ targetDisplayLabel(item) }} · {{ targetContextLabel(item) }}</small>
                </template>
                <template v-else>
                  <span>{{ targetDisplayLabel(item) }}</span>
                  <small>{{ targetContextLabel(item) }}</small>
                </template>
              </div>
            </template>
            <template v-slot:[`item.writes`]="{ item }">
              <span v-if="item.redacted" class="text--secondary"><v-icon x-small>mdi-eye-off-outline</v-icon> Hidden</span>
              <div v-else-if="actionWrites(item).length" class="run-field-summary py-1">
                <span>{{ writeSummary(item) }}</span>
                <small v-if="item.fieldOutcomesResolved.length" class="run-field-summary__attention">
                  {{ item.fieldOutcomesResolved.length }} {{ item.fieldOutcomesResolved.length === 1 ? 'field needs' : 'fields need' }} attention
                </small>
              </div>
              <div v-else-if="item.refusal" class="run-field-summary run-field-summary--refusal py-1">
                <span :title="item.refusal.text">{{ item.refusal.text }}</span>
                <small :title="item.refusal.action">{{ item.refusal.action }}</small>
              </div>
              <span v-else class="run-field-summary run-field-summary--none">{{ item.reasonLabel }}</span>
            </template>
            <template v-slot:[`item.result`]="{ item }">
              <span class="run-result" :class="`run-result--${item.result.tone}`">
                <v-icon v-if="item.result.icon" small>{{ item.result.icon }}</v-icon>{{ item.result.label }}
              </span>
            </template>
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length" class="run-ledger__cell">
                <div class="run-ledger__block">
                  <div v-if="item.redacted" class="run-ledger__restricted">
                    <v-icon x-small>mdi-eye-off-outline</v-icon> {{ item.redactionLabel }}
                  </div>
                  <template v-else>
                    <div v-if="actionWrites(item).length" class="run-ledger__row run-ledger__row--head" aria-hidden="true">
                      <span>Property</span><span>Current</span><span>After sync</span>
                    </div>
                    <div v-for="write in actionWrites(item)" :key="write.key" class="run-ledger__row">
                      <span class="run-ledger__prop">{{ write.destination }}<small>{{ write.namespace }}</small></span>
                      <template v-if="write.isLink && item.repoint">
                        <span class="run-ledger__val">
                          <relationship-tag-link
                            previous
                            :name="item.repoint.previous.name"
                            :target-id="item.repoint.previous.targetId"
                            :facility-id="item.repoint.previous.facilityId" />
                        </span>
                        <span class="run-ledger__val">
                          <relationship-tag-link
                            :name="item.repoint.next.name"
                            :target-id="item.repoint.next.targetId"
                            :facility-id="item.repoint.next.facilityId" />
                        </span>
                      </template>
                      <template v-else>
                        <span class="run-ledger__val run-ledger__val--muted">
                          <template v-if="write.hasCurrentValue">{{ write.current }}</template>
                          <template v-else>— <small>set after approval</small></template>
                        </span>
                        <span
                          class="run-ledger__val"
                          :class="writeChanged(write) ? 'run-ledger__val--proposed' : 'run-ledger__val--muted'">{{ write.proposed }}</span>
                      </template>
                    </div>
                    <div
                      v-for="(outcome, index) in item.fieldOutcomesResolved"
                      :key="`${item.planItemKey}-outcome-${index}`"
                      class="run-ledger__issue">
                      <v-icon x-small color="warning">mdi-alert-circle-outline</v-icon>{{ fieldOutcomeLabel(outcome) }}
                    </div>
                    <relationship-run-explain
                      v-if="item.refusal"
                      :text="item.refusal.text"
                      :action="item.refusal.action"
                      :code="item.refusal.code" />
                    <relationship-run-explain
                      v-else-if="item.repointExplanation"
                      :label="item.repointExplanation.label"
                      :text="item.repointExplanation.text"
                      :action="item.repointExplanation.action" />
                    <div v-else-if="!actionWrites(item).length && !item.fieldOutcomesResolved.length" class="run-ledger__restricted">
                      {{ item.reasonLabel }}
                    </div>
                  </template>
                </div>
              </td>
            </template>
            <template v-slot:no-data>
              <div class="ds-empty-state py-7">
                <v-icon class="ds-empty-state__icon">mdi-format-list-bulleted-square</v-icon>
                <div class="ds-empty-state__title">{{ emptyTitle }}</div>
                <div class="ds-empty-state__description">
                  {{ emptyDescription }}
                </div>
              </div>
            </template>
          </v-data-table>

          <div v-if="visibleItemsCursor" class="d-flex align-center justify-center mt-3">
            <v-btn outlined color="primary" :loading="visibleItemsLoading" @click="loadMoreVisibleItems">
              <v-icon left>mdi-chevron-down</v-icon>Load more decisions
            </v-btn>
          </div>

          <div v-if="recoveryGuidance" class="run-recovery mt-5" :class="`run-recovery--${recoveryGuidance.color}`" role="status" aria-live="polite">
            <v-icon :color="recoveryGuidance.color">{{ recoveryGuidance.icon }}</v-icon>
            <div>
              <strong>{{ recoveryGuidance.title }}</strong>
              <span>{{ recoveryGuidance.detail }}</span>
            </div>
            <v-btn v-if="recoveryAction" small outlined :color="recoveryGuidance.color" @click="handleRecoveryAction">
              {{ recoveryAction.label }}
            </v-btn>
          </div>

          <div
            v-if="historyOnly && isTerminal && itemView === 'attention' && attentionTotal !== null"
            class="ds-info-box ds-info-box--warning run-failed-rows mt-5">
            <v-icon color="warning">mdi-alert-box-outline</v-icon>
            <div class="flex-grow-1">
              <strong>{{ attentionItems.length }} of {{ attentionTotalLabel }} attention decisions loaded</strong>
              <div>Open these rows in the Data tab to review and correct them.</div>
            </div>
            <v-btn small outlined color="warning" @click="showAttentionRowsInData">
              <v-icon left small>mdi-table-filter</v-icon>Show attention rows in Data
            </v-btn>
          </div>
        </div>
      </v-card-text>

      <section v-if="awaitingConfirmation && !historyOnly" class="confirmation-boundary" aria-label="Preview approval">
        <div v-if="!allItemsLoaded" class="confirmation-boundary__message" role="alert">
          <v-icon small color="warning">mdi-book-open-page-variant-outline</v-icon>
          <span>Load all row decisions before approval. {{ loadedItemsLabel }}.</span>
        </div>
        <div v-else-if="!hasCompleteDecisionFacts" class="confirmation-boundary__message" role="alert">
          <v-icon small color="warning">mdi-file-alert-outline</v-icon>
          <span>Some decision details are incomplete. Build a new preview before approval.</span>
        </div>
        <div v-else-if="!hasValidGroupBinding" class="confirmation-boundary__message" role="alert">
          <v-icon small color="warning">mdi-account-group-outline</v-icon>
          <span>{{ groupSubject }} changed or could not be verified. Build a new preview.</span>
        </div>
        <div v-else-if="!canConfirmPermission" class="confirmation-boundary__message" role="alert">
          <v-icon small color="warning">mdi-lock-outline</v-icon>
          <span>You do not have permission to approve this preview.</span>
        </div>
        <v-checkbox
          v-model="reviewed"
          class="confirmation-boundary__checkbox"
          hide-details
          :disabled="isRedacted || isExpired || !hasValidPreviewEnvelope || !allItemsLoaded || !hasCompleteDecisionFacts || !hasValidGroupBinding || !canConfirmPermission || confirmBlockingProblem"
          label="I reviewed every row decision and field change in this preview."></v-checkbox>
        <span class="confirmation-boundary__hint">Approval applies once to this saved preview.</span>
      </section>

      <footer class="ds-dialog__footer">
        <v-btn text :disabled="confirming || cancelling" @click="close">Close</v-btn>
        <v-btn
          v-if="awaitingConfirmation && canCancelPermission"
          text
          color="error"
          :loading="cancelling"
          :disabled="confirming"
          @click="cancelPreview">
          <v-icon left>mdi-cancel</v-icon>Cancel preview
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          v-if="!currentRun && !historyOnly"
          color="primary"
          depressed
          :loading="previewing"
          :disabled="!canPreview"
          @click="buildPreview">
          <v-icon left>mdi-shield-search-outline</v-icon>{{ isFieldSync ? 'Preview updates' : 'Build preview' }}
        </v-btn>
        <v-btn
          v-else-if="awaitingConfirmation && canRepreview"
          color="primary"
          text
          @click="repreview">
          <v-icon left>mdi-refresh</v-icon>Re-preview
        </v-btn>
        <v-btn
          v-else-if="awaitingConfirmation && !historyOnly"
          color="primary"
          depressed
          :loading="confirming"
          :disabled="!canConfirm"
          @click="confirm">
          <v-icon left>mdi-shield-check-outline</v-icon>Approve and run
        </v-btn>
        <v-btn v-else-if="isTerminal && !historyOnly" color="primary" text @click="repreview">
          <v-icon left>mdi-refresh</v-icon>New preview
        </v-btn>
      </footer>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex';
import { RELATIONSHIP_PREVIEW_MODES } from '../../services/registerRelationshipService';
import {
  relationshipAttentionFilter,
  RELATIONSHIP_RUN_TERMINAL_STATUSES,
  runStatusMeta,
  RELATIONSHIP_LINK_DESTINATION_KIND,
} from '../../config/relationshipUi';
import {
  groupRowRefusals,
  problemCopy,
  repointExplanation,
  repointTargets,
  rowRefusalCopy,
  rowRefusalKey,
  runItemResult,
} from '../../config/relationshipRefusalCopy';
import RelationshipRefusalBanner from './RelationshipRefusalBanner.vue';
import RelationshipRefusalStrip from './RelationshipRefusalStrip.vue';
import RelationshipRepointCell from './RelationshipRepointCell.vue';
import RelationshipRunExplain from './RelationshipRunExplain.vue';
import RelationshipTagLink from './RelationshipTagLink.vue';

// Why a link cell did not become a connection, as emitted by the plan compiler
// (ForgeTagRelationshipAdapterCodes). A link cell names another record, so these are resolution
// failures rather than value problems, and each one leaves the row unchanged.
const LINK_OUTCOME_CODES = Object.freeze({
  unresolved: 'linkTargetUnresolved',
  ambiguous: 'linkTargetAmbiguous',
  unavailable: 'linkTargetUnavailable',
});

// A value the row supplied could not be turned into what the destination stores.
const CONVERSION_FAILED_CODE = 'conversionFailed';

// Codes the compiler reports one level down: a row stopped by one of these is published under the
// generic `unresolvedMapping` code with the real one in its reason data. Reading only the outer code
// would tell the operator a mapping is missing when the mapping is fine and the value is not.
const NESTED_ROW_OUTCOME_CODES = Object.freeze([
  ...Object.values(LINK_OUTCOME_CODES),
  CONVERSION_FAILED_CODE,
]);

const RUN_POLL_INTERVAL_MS = 2500;
const RUN_MONITOR_WINDOW_MS = 20 * 60 * 1000;
const RUN_STATUS_REQUEST_TIMEOUT_MS = 15 * 1000;
const RUN_MAX_POLL_ATTEMPTS = Math.ceil(RUN_MONITOR_WINDOW_MS / RUN_POLL_INTERVAL_MS);

export default {
  name: 'RelationshipRunDialog',
  components: {
    RelationshipRefusalBanner,
    RelationshipRefusalStrip,
    RelationshipRepointCell,
    RelationshipRunExplain,
    RelationshipTagLink,
  },
  props: {
    // Register owner from registerOwnershipScope(): { kind, label }. Names the Facility Group in copy.
    ownerScope: { type: Object, default: null },
    value: { type: Boolean, default: false },
    relationship: { type: Object, required: true },
    selectedRowIds: { type: Array, default: () => [] },
    dataFilterSnapshot: { type: Object, default: () => ({}) },
    reconcile: { type: Boolean, default: false },
    previewMode: {
      type: String,
      default: RELATIONSHIP_PREVIEW_MODES.RESOLVE,
      validator: value => Object.values(RELATIONSHIP_PREVIEW_MODES).includes(value),
    },
    confirmAllowed: { type: Boolean, default: false },
    cancelAllowed: { type: Boolean, default: false },
    targetScopeKind: { type: String, default: null },
    initialRunId: { type: String, default: null },
    historyOnly: { type: Boolean, default: false },
  },
  data() {
    return {
      scopeKind: 'register',
      reviewed: false,
      now: Date.now(),
      clock: null,
      poller: null,
      pollingAbortController: null,
      pollingActive: false,
      pollingGeneration: 0,
      pollingProblem: false,
      pollingStartedAt: null,
      pollingAttempts: 0,
      pollingExhausted: false,
      terminalNotificationRunId: null,
      itemView: 'all',
      expandedItems: [],
      attentionItems: [],
      attentionCursor: null,
      attentionTotal: null,
      loadingAttentionItems: false,
      refusalFilter: null,
    };
  },
  computed: {
    ...mapState('REGISTERS/relationships', [
      'currentRun',
      'currentRunItems',
      'currentRunItemsCursor',
      'currentRunItemsTotal',
      'loadingRunItems',
      'previewing',
      'confirming',
      'cancelling',
      'problem',
    ]),
    isFieldSync() {
      return !this.reconcile && !this.historyOnly
        && this.previewMode === RELATIONSHIP_PREVIEW_MODES.SYNC_FIELDS;
    },
    dialogIcon() {
      if (this.historyOnly) return 'mdi-history';
      if (this.reconcile) return 'mdi-refresh';
      return this.isFieldSync ? 'mdi-table-sync' : 'mdi-play-circle-outline';
    },
    dialogTitle() {
      if (this.historyOnly) return 'Run history detail';
      if (this.reconcile) return 'Reconcile relationship';
      return this.isFieldSync ? 'Sync mapped fields' : 'Preview relationship run';
    },
    dialogSubtitle() {
      if (this.historyOnly) return 'Previous run details';
      if (this.reconcile) return 'Retry unresolved connections without creating records';
      return this.isFieldSync
        ? 'Compare current and proposed values before approval'
        : 'Review every row before approval';
    },
    itemHeaders() {
      const headers = [
        { text: '', value: 'data-table-expand', sortable: false, width: 44 },
        { text: 'Record', value: 'row', sortable: false, width: 210 },
        { text: 'Decision', value: 'decision', sortable: false, width: 220 },
        { text: 'Related record', value: 'target', sortable: false, width: 230 },
        { text: 'Field changes', value: 'writes', sortable: false },
      ];
      // Each row's end status once the run has finished (terminalClass; no extra API).
      return this.isTerminal
        ? [...headers, { text: 'Result', value: 'result', sortable: false, width: 170 }]
        : headers;
    },
    ownerKind() { return this.ownerScope?.kind || this.targetScopeKind || null; },
    groupName() {
      const label = typeof this.ownerScope?.label === 'string' ? this.ownerScope.label.trim() : '';
      // registerOwnershipScope() falls back to the bare noun when the name is unknown.
      return this.ownerKind === 'facilityGroup' && label && label !== 'Facility Group' ? label : '';
    },
    groupSubject() { return this.groupName || 'The Facility Group'; },
    copyContext() {
      return {
        groupName: this.groupName,
        registerKind: this.ownerKind,
        tagTypeLabel: this.targetTypeLabel === 'Related record' ? '' : this.targetTypeLabel,
      };
    },
    problemCopyResolved() { return this.problem ? problemCopy(this.problem, this.copyContext) : null; },
    facilityNames() { return this.$store.getters.getFacilityDict || {}; },
    refusalSummary() {
      const items = this.itemView === 'attention' ? this.attentionItems : this.currentRunItems;
      return groupRowRefusals(items, this.copyContext);
    },
    emptyTitle() {
      if (this.refusalFilter) return 'No rows match this reason';
      return this.itemView === 'attention' && this.attentionTotal === 0 ? 'No rows need attention' : 'No run items were returned';
    },
    emptyDescription() {
      if (this.refusalFilter) return 'Show all rows to see every decision.';
      return this.itemView === 'attention' && this.attentionTotal === 0
        ? 'All reviewed rows completed without issues.'
        : 'Nothing can be approved until the preview returns row decisions.';
    },
    runId() { return this.currentRun?.id; },
    status() { return this.currentRun?.status || null; },
    statusMeta() { return runStatusMeta(this.status); },
    problemMessage() {
      const messages = {
        REGISTER_NOT_ACTIVE: 'This register is not active. Activate the register, then build a new preview.',
        PREVIEW_STALE: 'The data changed after this preview was built. Build a new preview to review the latest decisions.',
        PREVIEW_EXPIRED: 'This preview expired. Build a new preview before approval.',
        RUN_NOT_CONFIRMABLE: 'This run is no longer awaiting approval. Refresh the run history for its latest status.',
        RUN_NOT_CANCELABLE: 'This preview changed before it could be cancelled. Its latest status is now shown.',
        TARGET_SCOPE_UNSUPPORTED: 'These Tags can’t be used here.',
        INVALID_FILTER_SCOPE: 'The Data filters changed or are no longer valid. Return to Data and try again.',
        FILTERED_SCOPE_UNAVAILABLE: 'The filtered rows could not be verified. Return to Data and try again.',
        PREVIEW_SCOPE_TOO_LARGE: 'Too many rows match this preview. Narrow the filters in Data and try again.',
        FACILITY_VIEW_REQUIRED: 'Some Facilities are hidden from you, so approval is locked.',
        TARGET_VIEW_REQUIRED: 'You do not have access to all related-record details needed for this preview.',
      };
      return messages[this.problem?.code]
        || 'Try again or build a new preview. Nothing was changed without your approval.';
    },
    runConfigSnapshot() { return this.currentRun?.configSnapshot || this.relationship || {}; },
    targetTypeLabel() {
      return this.safeFieldLabel(this.runConfigSnapshot.targetDisplayName
        || this.runConfigSnapshot.targetKind, 'Related record');
    },
    awaitingConfirmation() { return this.status === 'awaitingConfirmation'; },
    isExecuting() { return this.status === 'queued' || this.status === 'running'; },
    isTerminal() { return RELATIONSHIP_RUN_TERMINAL_STATUSES.includes(this.status); },
    expiresAt() {
      if (!this.currentRun?.expiresAt) return null;
      const timestamp = new Date(this.currentRun.expiresAt).getTime();
      return Number.isFinite(timestamp) ? timestamp : null;
    },
    remainingSeconds() { return this.expiresAt ? Math.max(0, Math.floor((this.expiresAt - this.now) / 1000)) : 0; },
    remainingLabel() {
      const minutes = Math.floor(this.remainingSeconds / 60);
      const seconds = String(this.remainingSeconds % 60).padStart(2, '0');
      return this.expiresAt ? `${minutes}:${seconds} remaining` : 'Invalid preview expiry';
    },
    isExpired() { return this.expiresAt === null || this.remainingSeconds <= 0; },
    showHeaderExpiry() { return this.awaitingConfirmation && !this.historyOnly && this.expiresAt !== null; },
    hasValidPreviewEnvelope() {
      return Boolean(
        this.runId
        && typeof this.currentRun?.planHash === 'string'
        && this.currentRun.planHash.trim()
        && this.expiresAt !== null,
      );
    },
    actionCount() { return this.currentRun?.plannedActionCount ?? 0; },
    outcomeCount() { return this.currentRun?.plannedOutcomeCount ?? 0; },
    failedCount() { return this.currentRun?.failedCount ?? 0; },
    driftedCount() { return this.currentRun?.driftedCount ?? 0; },
    isRedacted() {
      return this.currentRun?.confirmBlocked === true
        || this.currentRun?.targetRedacted === true
        || this.currentRunItems.some((item) => item.targetRedacted === true);
    },
    redactionSummary() {
      const codes = new Set([
        this.currentRun?.confirmBlockedCode,
        ...this.currentRunItems.map((item) => item?.confirmBlockedCode),
        ...this.attentionItems.map((item) => item?.confirmBlockedCode),
      ].filter(Boolean));
      if (codes.has('FACILITY_VIEW_REQUIRED') && codes.has('TARGET_VIEW_REQUIRED')) {
        return 'Some Facility and related-record details are hidden by your access.';
      }
      if (codes.has('FACILITY_VIEW_REQUIRED')) {
        return 'Some Facility details and source values are hidden because you do not have access to them.';
      }
      if (codes.has('TARGET_VIEW_REQUIRED')) {
        return 'Some related-record details are hidden because you do not have access to them.';
      }
      return 'At least one row contains restricted details that are not available in this view.';
    },
    normalizedFilterSnapshot() {
      const snapshot = this.dataFilterSnapshot || {};
      const search = typeof snapshot.search === 'string' ? snapshot.search.trim() : '';
      const columnFilters = snapshot.columnFilters && typeof snapshot.columnFilters === 'object'
        && !Array.isArray(snapshot.columnFilters)
        ? { ...snapshot.columnFilters }
        : {};
      return {
        generation: Number(snapshot.generation),
        requestId: snapshot.requestId,
        status: snapshot.status,
        search,
        columnFilters,
        totalCount: Number.isInteger(snapshot.totalCount) && snapshot.totalCount >= 0
          ? snapshot.totalCount
          : null,
        hasFilters: Boolean(search || Object.keys(columnFilters).length),
      };
    },
    filterSnapshotReady() {
      const snapshot = this.normalizedFilterSnapshot;
      return Number.isInteger(snapshot.generation)
        && snapshot.generation > 0
        && typeof snapshot.requestId === 'string'
        && Boolean(snapshot.requestId)
        && snapshot.status === 'ready'
        && snapshot.totalCount !== null;
    },
    filteredRowsCount() {
      return this.filterSnapshotReady && this.normalizedFilterSnapshot.hasFilters
        ? this.normalizedFilterSnapshot.totalCount
        : null;
    },
    filteredRowsAvailable() {
      return this.filterSnapshotReady
        && this.normalizedFilterSnapshot.hasFilters
        && this.normalizedFilterSnapshot.totalCount > 0;
    },
    filteredRowsDescription() {
      const snapshot = this.normalizedFilterSnapshot;
      if (!snapshot.hasFilters) return 'Filter or search in Data first';
      if (snapshot.status === 'loading') return 'Waiting for the latest filtered results from Data';
      if (snapshot.status === 'error') return 'Filtered results could not be verified. Return to Data and try again';
      if (!this.filterSnapshotReady) return 'Return to Data to refresh the filtered results';
      if (snapshot.totalCount === 0) return 'No rows match the current Data filters';
      return `All ${snapshot.totalCount} matching rows, including rows on other pages`;
    },
    canPreview() {
      if (this.historyOnly) return false;
      if (this.previewing) return false;
      if (this.reconcile) return true;
      return this.scopeKind === 'register'
        || (this.scopeKind === 'rows' && this.selectedRowIds.length > 0)
        || (this.scopeKind === 'filtered' && this.filteredRowsAvailable);
    },
    canConfirm() {
      return this.reviewed
        && !this.historyOnly
        && this.canConfirmPermission
        && this.allItemsLoaded
        && this.hasCompleteDecisionFacts
        && this.hasValidGroupBinding
        && !this.confirming
        && !this.isRedacted
        && !this.isExpired
        && this.hasValidPreviewEnvelope
        && !this.confirmBlockingProblem
        && this.awaitingConfirmation;
    },
    canConfirmPermission() { return this.confirmAllowed === true; },
    canCancelPermission() { return this.cancelAllowed === true; },
    confirmationBlockers() {
      const blockers = [];
      if (!this.runId) blockers.push('This preview could not be identified. Build a new preview.');
      if (!this.nonEmptyString(this.currentRun?.planHash)) blockers.push('The preview integrity check is missing. Build a new preview.');
      if (this.expiresAt === null) blockers.push('The preview expiry is missing or invalid.');
      else if (this.isExpired) blockers.push('The preview has expired; build a new preview.');
      if (!this.allItemsLoaded) blockers.push(`Load all row decisions (${this.loadedItemsLabel}).`);
      if (this.allItemsLoaded && !this.hasCompleteDecisionFacts) {
        blockers.push('At least one row is missing details required for safe approval. Build a new preview.');
      }
      if (!this.hasValidGroupBinding) blockers.push('Facility Group membership could not be verified.');
      if (this.isRedacted) blockers.push('Restricted details prevent approval.');
      if (!this.canConfirmPermission) blockers.push('You do not have permission to approve this preview.');
      if (this.confirmBlockingProblem) blockers.push('This preview has an error. Build a new preview instead of approving it.');
      return blockers;
    },
    expectedItemCount() {
      if (this.validCandidateCount(this.currentRunItemsTotal)) {
        return Number(this.currentRunItemsTotal);
      }
      const total = Number(this.actionCount) + Number(this.outcomeCount);
      return total > 0 ? total : null;
    },
    allItemsLoaded() {
      if (this.currentRunItemsCursor) return false;
      return this.expectedItemCount !== null
        && this.expectedItemCount > 0
        && this.currentRunItems.length === this.expectedItemCount;
    },
    hasCompleteDecisionFacts() {
      if (!this.currentRunItems.length) return false;
      return this.currentRunItems.every((item) => {
        const row = item?.rowSnapshot;
        if (!Number.isInteger(item?.planItemKey) || item.planItemKey < 1) return false;
        if (!row || !this.nonEmptyString(row.rowId) || !this.nonEmptyString(row.sourceFieldKey)
          || !this.nonEmptyString(row.sourceFingerprint)
          || !Object.prototype.hasOwnProperty.call(row, 'sourceValue')) return false;
        if (!['action', 'outcome'].includes(item.itemKind)) return false;
        if (!['facility', 'facilityGroup'].includes(item.targetScopeKind) || !this.nonEmptyString(item.targetScopeId)) return false;
        if (!this.validCandidateCount(item.candidateCount)
          || typeof item.targetRedacted !== 'boolean'
          || typeof item.confirmBlocked !== 'boolean') return false;

        if (item.itemKind === 'action') {
          const validAction = ['create', 'link', 'syncFields', 'repoint', 'cleanup'].includes(item.action);
          const validAuthority = ['grant', 'none'].includes(item.mapAuthorityDisposition);
          const validHash = this.nonEmptyString(item.actionPayloadHash);
          const validCreateIdentity = item.action !== 'create' || this.nonEmptyString(item.createMatchKey);
          const validChosenTarget = this.nonEmptyString(item.chosenTargetKind)
            && (item.action === 'create' || this.nonEmptyString(item.chosenTargetId));
          const validExpectedLink = !['syncFields', 'repoint', 'cleanup'].includes(item.action)
            ? item.expectedLink === null || item.expectedLink === undefined
            : this.hasExpectedLinkTriple(item.expectedLink);
          const validDestinations = this.validCompiledDestinations(
            item.actionPayload,
            item.action === 'syncFields' || (this.isFieldSync && item.action === 'link'),
          );
          return validAction && validAuthority && validHash && validCreateIdentity && validChosenTarget && validExpectedLink
            && item.preconditions && typeof item.preconditions === 'object'
            && validDestinations && this.validFieldOutcomes(item.fieldOutcomes);
        }

        return this.nonEmptyString(item.outcomeCode)
          && this.validReason(item.reason);
      });
    },
    loadedItemsLabel() {
      return this.expectedItemCount === null
        ? `${this.currentRunItems.length} loaded`
        : `${this.currentRunItems.length} of ${this.expectedItemCount} loaded`;
    },
    confirmBlockingProblem() { return this.awaitingConfirmation && Boolean(this.problem); },
    hasValidGroupBinding() {
      return this.currentRunItems.every((item) => (
        item?.targetScopeKind === 'facilityGroup'
          ? this.nonEmptyString(item.groupScopeFingerprint)
          : item?.groupScopeFingerprint === null || item?.groupScopeFingerprint === undefined
      ));
    },
    canRepreview() {
      return !this.historyOnly
        && Boolean(this.currentRun)
        && (this.isExpired || ['PREVIEW_STALE', 'PREVIEW_EXPIRED'].includes(this.problem?.code));
    },
    progressPercent() {
      const total = this.actionCount + this.outcomeCount;
      const done = Number(this.currentRun?.succeededCount || 0)
        + Number(this.currentRun?.fieldErrorCount || 0)
        + Number(this.currentRun?.failedCount || 0)
        + Number(this.currentRun?.driftedCount || 0);
      return total ? Math.min(100, (done / total) * 100) : 0;
    },
    displayItems() {
      const source = this.itemView === 'attention' ? this.attentionItems : this.currentRunItems;
      // The reason filter narrows only what is shown; approval gates still read currentRunItems.
      const items = this.refusalFilter
        ? source.filter((item) => rowRefusalKey(item) === this.refusalFilter)
        : source;
      // Rows in register order (the server returns plan order, e.g. K2, K3, K1).
      return items
        .map((item) => this.presentItem(item))
        .sort((a, b) => this.rowOrderOf(a) - this.rowOrderOf(b));
    },
    visibleItemsLabel() {
      if (this.itemView === 'attention') {
        return this.attentionTotal === null
          ? `${this.attentionItems.length} attention items loaded`
          : `${this.attentionItems.length} of ${this.attentionTotal} attention items`;
      }
      return this.loadedItemsLabel;
    },
    visibleItemsCursor() {
      return this.itemView === 'attention' ? this.attentionCursor : this.currentRunItemsCursor;
    },
    visibleItemsLoading() {
      return this.itemView === 'attention' ? this.loadingAttentionItems : this.loadingRunItems;
    },
    attentionTotalLabel() { return this.attentionTotal === null ? 'unknown' : this.attentionTotal; },
    recoveryGuidance() {
      const guidance = {
        completedWithErrors: {
          title: 'Some rows need attention',
          detail: this.driftedCount
            ? 'Some records changed after the preview was approved, so they were left unchanged. Review them, then use Preview run again.'
            : 'Review the attention rows and their field details, then use Preview run again.',
          color: 'warning',
          icon: 'mdi-alert-circle-outline',
        },
        failed: {
          title: 'Execution stopped',
          detail: 'Rows completed before the error stay completed. Resolve the cause, then build a new preview.',
          color: 'error',
          icon: 'mdi-close-circle-outline',
        },
        expired: {
          title: 'Preview expired',
          detail: 'This plan can no longer be approved. Build a new preview to recalculate every row decision.',
          color: 'warning',
          icon: 'mdi-clock-alert-outline',
        },
        cancelled: {
          title: 'Run cancelled',
          detail: 'No resume is available. Build a new preview when the relationship is ready.',
          color: 'grey',
          icon: 'mdi-cancel',
        },
        superseded: {
          title: 'A newer run superseded this one',
          detail: 'A newer run is now current. Return to run history to review it; this run cannot be resumed.',
          color: 'grey',
          icon: 'mdi-swap-horizontal',
        },
      };
      return guidance[this.status] || null;
    },
    recoveryAction() {
      if (!this.recoveryGuidance) return null;
      if (this.historyOnly && ['completedWithErrors', 'failed'].includes(this.status)) {
        return { kind: 'attention', label: 'Review attention rows' };
      }
      if (!this.historyOnly && (this.isTerminal || this.status === 'expired')) {
        return { kind: 'repreview', label: 'Build a new preview' };
      }
      return null;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) this.open();
        else this.stopTimers();
      },
    },
    status(status, previousStatus) {
      if (status === 'queued' || status === 'running') this.startPolling();
      else this.stopPolling();
      // The poll that observes the terminal status commits it before REFRESH_CURRENT_RUN
      // finishes, and stopPolling() above aborts that request — so the refresh never reloaded
      // the row results or announced the terminal run (rows stayed "Not run", the card stayed
      // "Awaiting confirmation", the grid kept stale links). Handle the transition here, where
      // every path (poll, or a confirm that already returns a terminal run) passes.
      if (RELATIONSHIP_RUN_TERMINAL_STATUSES.includes(status)
        && previousStatus
        && !RELATIONSHIP_RUN_TERMINAL_STATUSES.includes(previousStatus)) {
        this.onRunReachedTerminal();
      }
    },
    runId() {
      this.expandedItems = [];
      this.refusalFilter = null;
    },
    itemView(view) {
      this.refusalFilter = null;
      if (view === 'attention' && this.attentionTotal === null) this.loadAttentionItems(true);
    },
  },
  beforeDestroy() { this.stopTimers(); },
  methods: {
    async open() {
      this.scopeKind = this.selectedRowIds.length ? 'rows' : 'register';
      this.reviewed = false;
      this.itemView = 'all';
      this.expandedItems = [];
      this.refusalFilter = null;
      this.attentionItems = [];
      this.attentionCursor = null;
      this.attentionTotal = null;
      this.pollingProblem = false;
      this.pollingStartedAt = null;
      this.pollingAttempts = 0;
      this.pollingExhausted = false;
      this.terminalNotificationRunId = null;
      this.now = Date.now();
      this.$store.dispatch('REGISTERS/relationships/CLEAR_CURRENT_RUN');
      this.clock = window.setInterval(() => { this.now = Date.now(); }, 1000);
      if (this.initialRunId) {
        try {
          await this.$store.dispatch('REGISTERS/relationships/LOAD_RUN', this.initialRunId);
        } catch (err) {
          // Typed ProblemDetails is rendered from Vuex.
        }
      }
      this.$nextTick(() => this.$refs.dialogTitle?.focus());
    },
    stopPolling({ resetMonitor = true } = {}) {
      this.pollingActive = false;
      this.pollingGeneration += 1;
      if (this.poller) window.clearTimeout(this.poller);
      this.poller = null;
      if (this.pollingAbortController) this.pollingAbortController.abort();
      this.pollingAbortController = null;
      if (resetMonitor) {
        this.pollingStartedAt = null;
        this.pollingAttempts = 0;
        this.pollingExhausted = false;
      }
    },
    stopTimers() {
      if (this.clock) window.clearInterval(this.clock);
      this.clock = null;
      this.stopPolling();
    },
    close() {
      this.stopTimers();
      this.$emit('input', false);
    },
    scopeRequest() {
      if (this.reconcile) return { kind: 'reconcile' };
      if (this.scopeKind === 'rows') {
        return this.selectedRowIds.length ? { kind: 'rows', rowIds: [...this.selectedRowIds] } : null;
      }
      if (this.scopeKind === 'filtered') {
        if (!this.filteredRowsAvailable) return null;
        const snapshot = this.normalizedFilterSnapshot;
        const dataQuery = {
          ...(snapshot.search ? { search: snapshot.search } : {}),
          ...(Object.keys(snapshot.columnFilters).length
            ? { columnFilters: { ...snapshot.columnFilters } }
            : {}),
        };
        return { kind: 'filtered', dataQuery };
      }
      return this.scopeKind === 'register' ? { kind: 'register' } : null;
    },
    async buildPreview() {
      if (this.historyOnly || !this.canPreview) return;
      const scope = this.scopeRequest();
      if (!scope) return;
      try {
        const run = await this.$store.dispatch('REGISTERS/relationships/PREVIEW_RUN', {
          relationshipId: this.relationship.id,
          scope,
          mode: this.previewMode,
        });
        if (run?.id) this.$emit('run-replaced', run);
      } catch (err) {
        // Typed ProblemDetails is rendered from Vuex.
      }
    },
    async confirm() {
      if (this.historyOnly || !this.canConfirm) return;
      try {
        const run = await this.$store.dispatch('REGISTERS/relationships/CONFIRM_CURRENT_RUN');
        this.notifyTerminalRun(run);
      } catch (err) {
        // Typed ProblemDetails is rendered from Vuex.
      }
    },
    async cancelPreview() {
      if (!this.awaitingConfirmation || !this.canCancelPermission || this.cancelling || this.confirming) return;
      try {
        const run = await this.$store.dispatch('REGISTERS/relationships/CANCEL_CURRENT_RUN');
        this.notifyTerminalRun(run);
      } catch (err) {
        // Typed ProblemDetails is rendered from Vuex and the preview remains reviewable.
      }
    },
    async refreshRun({ signal = null, timeout = null } = {}) {
      try {
        const run = await this.$store.dispatch('REGISTERS/relationships/REFRESH_CURRENT_RUN', {
          signal,
          timeout,
        });
        if (signal?.aborted || !run) return false;
        this.pollingProblem = false;
        this.notifyTerminalRun(run);
        return true;
      } catch (err) {
        if (signal?.aborted) return false;
        this.pollingProblem = true;
        return false;
      }
    },
    async onRunReachedTerminal() {
      const run = this.currentRun;
      if (!run?.id) return;
      this.notifyTerminalRun(run);
      try {
        await this.$store.dispatch('REGISTERS/relationships/LOAD_MORE_CURRENT_RUN_ITEMS', { reset: true });
      } catch (err) {
        // The terminal status stays authoritative; the typed problem is rendered from Vuex.
      }
    },
    notifyTerminalRun(run) {
      if (!run?.id || !RELATIONSHIP_RUN_TERMINAL_STATUSES.includes(run.status)) return;
      if (this.terminalNotificationRunId === run.id) return;
      this.terminalNotificationRunId = run.id;
      this.$emit('run-terminal', run);
    },
    retryPolling() {
      if (!this.isExecuting) return;
      this.pollingProblem = false;
      this.pollingExhausted = false;
      this.pollingStartedAt = Date.now();
      this.pollingAttempts = 0;
      this.startPolling();
    },
    async loadMoreItems() {
      try {
        await this.$store.dispatch('REGISTERS/relationships/LOAD_MORE_CURRENT_RUN_ITEMS');
      } catch (err) {
        // Typed ProblemDetails is rendered from Vuex.
      }
    },
    async loadAttentionItems(reset = false) {
      if (!this.runId || this.loadingAttentionItems) return;
      if (!reset && !this.attentionCursor) return;
      this.loadingAttentionItems = true;
      try {
        const page = await this.$store.dispatch('REGISTERS/relationships/LOAD_ATTENTION_RUN_ITEMS', {
          runId: this.runId,
          cursor: reset ? null : this.attentionCursor,
        });
        this.attentionItems = reset ? page.items : [...this.attentionItems, ...page.items];
        this.attentionCursor = page.nextCursor;
        this.attentionTotal = page.totalCount;
      } catch (err) {
        // Typed ProblemDetails is rendered from Vuex.
      } finally {
        this.loadingAttentionItems = false;
      }
    },
    loadMoreVisibleItems() {
      return this.itemView === 'attention' ? this.loadAttentionItems(false) : this.loadMoreItems();
    },
    showAttentionRowsInData() {
      if (!this.historyOnly) return;
      const filter = relationshipAttentionFilter(this.runId);
      if (!filter) return;
      this.close();
      this.$emit('show-attention-rows', filter);
    },
    startPolling() {
      if (this.pollingActive || this.pollingExhausted) return;
      if (!this.pollingStartedAt) this.pollingStartedAt = Date.now();
      this.pollingActive = true;
      const generation = ++this.pollingGeneration;
      this.pollOnce(generation);
    },
    exhaustPolling() {
      this.stopPolling({ resetMonitor: false });
      this.pollingExhausted = true;
      this.pollingProblem = true;
    },
    async pollOnce(generation) {
      if (!this.pollingActive || generation !== this.pollingGeneration || !this.isExecuting) return;
      const elapsedMs = Date.now() - this.pollingStartedAt;
      const remainingMs = RUN_MONITOR_WINDOW_MS - elapsedMs;
      if (remainingMs <= 0 || this.pollingAttempts >= RUN_MAX_POLL_ATTEMPTS) {
        this.exhaustPolling();
        return;
      }

      const controller = new AbortController();
      this.pollingAbortController = controller;
      this.pollingAttempts += 1;
      const succeeded = await this.refreshRun({
        signal: controller.signal,
        timeout: Math.max(1, Math.min(RUN_STATUS_REQUEST_TIMEOUT_MS, remainingMs)),
      });
      if (this.pollingAbortController === controller) this.pollingAbortController = null;
      if (generation !== this.pollingGeneration || !this.pollingActive) return;
      if (!succeeded) {
        const deadlineReached = Date.now() - this.pollingStartedAt >= RUN_MONITOR_WINDOW_MS;
        if (deadlineReached || this.pollingAttempts >= RUN_MAX_POLL_ATTEMPTS) this.exhaustPolling();
        else this.stopPolling({ resetMonitor: false });
        return;
      }
      if (succeeded && this.isExecuting) {
        const nextRemainingMs = RUN_MONITOR_WINDOW_MS - (Date.now() - this.pollingStartedAt);
        if (nextRemainingMs <= 0 || this.pollingAttempts >= RUN_MAX_POLL_ATTEMPTS) {
          this.exhaustPolling();
          return;
        }
        this.poller = window.setTimeout(() => {
          this.poller = null;
          this.pollOnce(generation);
        }, Math.min(RUN_POLL_INTERVAL_MS, nextRemainingMs));
      }
    },
    repreview() {
      if (this.historyOnly) return;
      this.stopPolling();
      this.$store.dispatch('REGISTERS/relationships/CLEAR_CURRENT_RUN');
      this.reviewed = false;
      this.itemView = 'all';
      this.expandedItems = [];
      this.refusalFilter = null;
      this.attentionItems = [];
      this.attentionCursor = null;
      this.attentionTotal = null;
      this.pollingProblem = false;
      this.terminalNotificationRunId = null;
    },
    handleRecoveryAction() {
      if (this.recoveryAction?.kind === 'repreview') {
        this.repreview();
        return;
      }
      if (this.recoveryAction?.kind === 'attention') {
        this.itemView = 'attention';
        if (this.attentionTotal === null) this.loadAttentionItems(true);
      }
    },
    humanizeCode(value) {
      if (typeof value !== 'string' || !value.trim()) return '';
      const words = value
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .trim();
      return words.charAt(0).toUpperCase() + words.slice(1);
    },
    isOpaqueIdentifier(value) {
      if (typeof value !== 'string') return false;
      const text = value.trim();
      return /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(text)
        || /(?:^|[^0-9a-f])[0-9a-f]{24,}(?:$|[^0-9a-f])/i.test(text);
    },
    safeFieldLabel(value, fallback = 'Field') {
      if (typeof value !== 'string' || !value.trim() || this.isOpaqueIdentifier(value)) return fallback;
      return this.humanizeCode(value) || fallback;
    },
    destinationLabel(destination) {
      const fallback = destination?.kind === 'native'
        ? 'Related record field'
        : destination?.kind === 'link'
          ? 'Linked record field'
          : 'Custom field';
      return this.safeFieldLabel(
        destination?.labelSnapshot || destination?.attributeKeySnapshot || destination?.fieldKey,
        fallback,
      );
    },
    formatOperatorValue(value) {
      if (value === undefined || value === null || value === '') return 'No value';
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      if (typeof value === 'number') return value.toLocaleString();
      if (typeof value === 'string') {
        const text = value.trim();
        return this.isOpaqueIdentifier(text) ? 'Reference value' : text;
      }
      if (Array.isArray(value)) {
        const readable = value.map(entry => this.formatOperatorValue(entry));
        return readable.length <= 3 ? readable.join(', ') : `${readable.length} values`;
      }
      return 'Configured value';
    },
    formatMappedValue(value) {
      if (value === undefined) return 'Value not provided';
      if (value === null) return 'No value';
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      if (typeof value === 'number') return value.toLocaleString();
      if (typeof value !== 'string') {
        try { return JSON.stringify(value); } catch (err) { return String(value); }
      }
      if (value === '') return 'Empty text';

      const text = value.trim();
      if (!text) return `Whitespace only (${value.length} ${value.length === 1 ? 'character' : 'characters'})`;

      const leadingWhitespace = value.match(/^\s+/)?.[0] || '';
      const trailingWhitespace = value.match(/\s+$/)?.[0] || '';
      const whitespaceNotes = [];
      if (leadingWhitespace) {
        const label = /^ +$/.test(leadingWhitespace)
          ? `leading space${leadingWhitespace.length === 1 ? '' : 's'}`
          : `leading whitespace character${leadingWhitespace.length === 1 ? '' : 's'}`;
        whitespaceNotes.push(`${leadingWhitespace.length} ${label}`);
      }
      if (trailingWhitespace) {
        const label = /^ +$/.test(trailingWhitespace)
          ? `trailing space${trailingWhitespace.length === 1 ? '' : 's'}`
          : `trailing whitespace character${trailingWhitespace.length === 1 ? '' : 's'}`;
        whitespaceNotes.push(`${trailingWhitespace.length} ${label}`);
      }
      return whitespaceNotes.length ? `${text} (${whitespaceNotes.join(', ')})` : value;
    },
    // An approval refusal can name the plan item that failed its check; mark that row.
    itemRowClass(item) {
      const flagged = Number.isInteger(this.problem?.planItemKey) && item?.planItemKey === this.problem.planItemKey;
      return flagged ? 'run-row--flagged' : '';
    },
    presentItem(item) {
      const sourceValueState = this.sourceValueState(item);
      const sourceValueLabel = this.sourceValueLabel(item);
      const recordLabel = this.recordLabel(item);
      const repoint = repointTargets(item);
      // Only a real value reads as "Matched from the source value “…”": hidden, unavailable and
      // placeholder labels (No value, Reference value) would misstate what was matched.
      const repointSourceValue = sourceValueState === 'available'
        && !['No value', 'Reference value', 'Configured value'].includes(sourceValueLabel)
        ? sourceValueLabel
        : '';
      return {
        ...item,
        redacted: item.targetRedacted === true,
        redactionLabel: this.redactionLabel(item),
        sourceValueState,
        sourceValueLabel,
        recordLabel,
        recordContext: this.recordContext(item),
        recordContextState: this.recordContextState(item),
        reasonLabel: this.reasonLabel(item),
        fieldOutcomesResolved: this.fieldOutcomes(item),
        refusal: rowRefusalCopy(item, this.refusalContext(item, recordLabel, sourceValueState, sourceValueLabel)),
        repoint,
        repointSourceValue,
        repointExplanation: repointExplanation(repoint, {
          sourceValue: repointSourceValue,
          terminalClass: item.terminalClass || null,
        }),
        result: runItemResult(item),
      };
    },
    // Per-row names for refusal copy: the row's Facility (known only when the plan carried its
    // Facility context) and the record's own key. Missing names fall back inside the copy module.
    refusalContext(item, recordLabel, sourceValueState, sourceValueLabel) {
      // A reason that names its own Facility (sameNameInMemberFacility) wins over the row's context.
      const reasonFacilityId = item?.reason?.data?.facilityId;
      const facilityId = typeof reasonFacilityId === 'string' && reasonFacilityId
        ? reasonFacilityId
        : (typeof item?.poiFacilityContextId === 'string' ? item.poiFacilityContextId : '');
      const facilityName = facilityId ? (this.facilityNames[facilityId] || this.facilityNames[facilityId.toLowerCase()] || '') : '';
      return {
        ...this.copyContext,
        facilityName: typeof facilityName === 'string' ? facilityName : '',
        recordName: /^Row \d+$|^Register row$/.test(recordLabel) ? '' : recordLabel,
        // The Tag name the row asks for (its matched source value), for copy that names the Tag.
        tagName: sourceValueState === 'available'
          && !['No value', 'Reference value', 'Configured value'].includes(sourceValueLabel)
          ? (sourceValueLabel || '')
          : '',
      };
    },
    recordLabel(item) {
      const recordKey = this.formatOperatorValue(item?.currentRecordKey);
      if (!['No value', 'Reference value', 'Configured value'].includes(recordKey)) return recordKey;

      const rowNumber = Number(item?.currentRowNumber);
      return Number.isInteger(rowNumber) && rowNumber > 0 ? `Row ${rowNumber}` : 'Register row';
    },
    recordContext(item) {
      const recordKey = this.formatOperatorValue(item?.currentRecordKey);
      const hasRecordKey = !['No value', 'Reference value', 'Configured value'].includes(recordKey);
      const rowNumber = Number(item?.currentRowNumber);
      const hasRowNumber = Number.isInteger(rowNumber) && rowNumber > 0;
      if (hasRecordKey && hasRowNumber) return `Row ${rowNumber}`;
      if (hasRecordKey || hasRowNumber) return 'Register record';

      const state = this.sourceValueState(item);
      return state === 'available' ? 'Record key unavailable' : this.sourceValueLabel(item);
    },
    recordContextState(item) {
      const recordKey = this.formatOperatorValue(item?.currentRecordKey);
      const hasRecordKey = !['No value', 'Reference value', 'Configured value'].includes(recordKey);
      const rowNumber = Number(item?.currentRowNumber);
      if (hasRecordKey || (Number.isInteger(rowNumber) && rowNumber > 0)) return 'available';
      return this.sourceValueState(item);
    },
    decisionLabel(item) {
      const labels = {
        create: 'Create related record',
        link: 'Connect existing record',
        syncFields: 'Update mapped fields',
        repoint: 'Change linked Tag',
        cleanup: 'Remove outdated link',
        alreadyLinked: 'Already connected',
        alreadyInSync: 'Already up to date',
        ambiguous: 'Multiple matches',
        unresolved: 'No match found',
        linkedTargetNotFound: 'Tag not found, link kept',
        sameNameInMemberFacility: 'Tag exists in a Facility',
        unresolvedMapping: 'Missing field mapping',
        outOfApplicability: 'Not applicable',
        sourceValueMissing: 'Source value missing',
        poiContextMissing: 'Facility not known for this row',
        sharedTargetConflict: 'Related record conflict',
        sharedTargetPayloadConflict: 'Conflicting field values',
        linkTargetUnresolved: 'No linked record found',
        linkTargetAmbiguous: 'Multiple linked records',
        linkTargetUnavailable: 'Link check unavailable',
        conversionFailed: 'Value cannot be used',
      };
      return labels[this.rowDecisionCode(item)] || 'Needs review';
    },
    decisionColor(item) {
      if (item?.itemKind === 'action') return 'primary';
      return this.outcomeColor(this.rowDecisionCode(item));
    },
    decisionIcon(item) {
      const icons = {
        create: 'mdi-plus-circle-outline',
        link: 'mdi-link-variant',
        syncFields: 'mdi-table-sync',
        repoint: 'mdi-swap-horizontal',
        cleanup: 'mdi-link-variant-off',
        alreadyLinked: 'mdi-check-circle-outline',
        alreadyInSync: 'mdi-check-circle-outline',
        ambiguous: 'mdi-alert-circle-outline',
        unresolved: 'mdi-help-circle-outline',
        linkedTargetNotFound: 'mdi-link-variant-off',
        sameNameInMemberFacility: 'mdi-content-duplicate',
        unresolvedMapping: 'mdi-file-alert-outline',
        outOfApplicability: 'mdi-minus-circle-outline',
        sourceValueMissing: 'mdi-file-question-outline',
        linkTargetUnresolved: 'mdi-link-variant-off',
        linkTargetAmbiguous: 'mdi-alert-circle-outline',
        linkTargetUnavailable: 'mdi-link-variant-off',
        conversionFailed: 'mdi-file-alert-outline',
      };
      return icons[this.rowDecisionCode(item)] || 'mdi-alert-circle-outline';
    },
    rowOrderOf(item) {
      const rowNumber = Number(item?.currentRowNumber);
      return Number.isInteger(rowNumber) && rowNumber > 0 ? rowNumber : Number.MAX_SAFE_INTEGER;
    },
    // The Tag's own name when the row names it: the chosen target's display name, else the source
    // value the row is matched by (what a create will name the new Tag). Placeholder labels such as
    // "No value" are never shown as a name.
    targetName(item) {
      if (!item || item.redacted) return '';
      const chosen = typeof item.chosenTargetDisplayName === 'string' ? item.chosenTargetDisplayName.trim() : '';
      if (chosen) return chosen;
      const named = ['create', 'link', 'syncFields', 'alreadyLinked', 'alreadyInSync'];
      if (!named.includes(this.rowDecisionCode(item))) return '';
      if (item.sourceValueState !== 'available') return '';
      const value = item.sourceValueLabel;
      return ['No value', 'Reference value', 'Configured value'].includes(value) ? '' : (value || '');
    },
    targetDisplayLabel(item) {
      const relatedType = this.targetTypeLabel;
      const labels = {
        create: `New ${relatedType}`,
        link: `Existing ${relatedType}`,
        syncFields: `Connected ${relatedType}`,
        repoint: `Replacement ${relatedType}`,
        cleanup: `Current ${relatedType}`,
        alreadyLinked: `Connected ${relatedType}`,
        alreadyInSync: `Connected ${relatedType}`,
        ambiguous: 'No record selected',
        unresolved: 'No record found',
        linkedTargetNotFound: `Current ${relatedType} kept`,
        sameNameInMemberFacility: 'No record created',
        unresolvedMapping: 'No record selected',
        outOfApplicability: 'Not required',
        sourceValueMissing: 'No record selected',
        poiContextMissing: 'No record selected',
        sharedTargetConflict: 'No record selected',
        sharedTargetPayloadConflict: 'No record selected',
        linkTargetUnresolved: 'No record found',
        linkTargetAmbiguous: 'No record selected',
        linkTargetUnavailable: 'No record selected',
        conversionFailed: 'No record selected',
      };
      return labels[this.rowDecisionCode(item)] || 'Needs review';
    },
    targetContextLabel(item) {
      if (item?.itemKind !== 'action') return 'No changes will be made';
      if (item.action === 'create') return 'Created only after approval';
      if (item.action === 'cleanup') return 'Connection will be removed';
      if (item.action === 'syncFields') return 'Mapped fields will update after approval';
      return 'Matched from the source value';
    },
    nonEmptyString(value) { return typeof value === 'string' && Boolean(value.trim()); },
    validCandidateCount(value) {
      if (value === null || value === undefined || value === '') return false;
      const count = Number(value);
      return Number.isInteger(count) && count >= 0;
    },
    hasExpectedLinkTriple(expectedLink) {
      return this.nonEmptyString(expectedLink?.rowVersion)
        && this.nonEmptyString(expectedLink?.associationState)
        && this.nonEmptyString(expectedLink?.oldTargetIdentityHash);
    },
    validReason(reason) {
      return Boolean(reason
        && typeof reason === 'object'
        && this.nonEmptyString(reason.code)
        && reason.data && typeof reason.data === 'object' && !Array.isArray(reason.data));
    },
    fieldOutcomes(item) {
      if (!Array.isArray(item?.fieldOutcomes)) return [];
      return item.fieldOutcomes.filter((outcome) => (
        outcome?.code || outcome?.reason?.code
      ) !== 'alreadyInSync');
    },
    validFieldOutcomes(value) {
      if (!Array.isArray(value)) return false;
      return value.every((outcome) => outcome
        && typeof outcome === 'object'
        && this.nonEmptyString(outcome.sourceFieldKey)
        && this.validDestinationIdentity(outcome.destination)
        && this.nonEmptyString(outcome.code)
        && this.validReason(outcome.reason));
    },
    validCompiledDestinations(payload, requireCurrentValue = false) {
      const destinations = payload?.destinations;
      if (!Array.isArray(destinations)) return false;
      return destinations.every((write) => {
        if (!write || typeof write !== 'object') return false;
        return this.validDestinationIdentity(write.destination)
          && Object.prototype.hasOwnProperty.call(write, 'convertedValue')
          && (!requireCurrentValue || Object.prototype.hasOwnProperty.call(write, 'currentValue'));
      });
    },
    validDestinationIdentity(destination) {
      return (destination?.kind === 'native' && this.nonEmptyString(destination.fieldKey))
        || (destination?.kind === 'link' && this.nonEmptyString(destination.fieldKey))
        || (destination?.kind === 'dynamicAttribute'
          && this.nonEmptyString(destination.storeKey)
          && this.nonEmptyString(destination.definitionRef)
          && this.nonEmptyString(destination.attributeKeySnapshot))
        || (destination?.kind === 'definedCustomField'
          && this.nonEmptyString(destination.storeKey)
          && this.nonEmptyString(destination.definitionId));
    },
    actionWrites(item) {
      const writes = Array.isArray(item?.actionPayload?.destinations)
        ? item.actionPayload.destinations
        : [];
      return writes
        .filter(write => write && typeof write === 'object')
        .map((write, index) => ({
          key: `${item.planItemKey}-write-${index}`,
          destination: this.destinationLabel(write.destination),
          namespace: this.destinationNamespace(write.destination),
          hasCurrentValue: Object.prototype.hasOwnProperty.call(write, 'currentValue'),
          isLink: write.destination?.kind === RELATIONSHIP_LINK_DESTINATION_KIND,
          valueChanged: !this.sameWriteValue(write.currentValue, write.convertedValue),
          current: this.writeValueLabel(write.destination, write.currentValue),
          proposed: this.writeValueLabel(write.destination, write.convertedValue, write.rawValue),
        }));
    },
    // A link writes an internal record id, which no reader can use. `rawValue` carries the name the
    // row named the record by, and is the only form worth showing; runs stored before the API
    // projected it have none, so those still describe the connection instead.
    //
    // The name is printed exactly as the register holds it. This column echoes stored values rather
    // than prose, and re-casing a tag identifier would misrepresent it.
    writeValueLabel(destination, value, rawValue) {
      if (destination?.kind !== RELATIONSHIP_LINK_DESTINATION_KIND) return this.formatMappedValue(value);
      if (value === undefined) return 'Value not provided';
      if (value === null || value === '') return 'Not connected';
      return this.nonEmptyString(rawValue)
        ? this.formatOperatorValue(rawValue)
        : 'Connected record';
    },
    sameWriteValue(left, right) {
      try { return JSON.stringify(left) === JSON.stringify(right); } catch (err) { return left === right; }
    },
    destinationNamespace(destination) {
      if (destination?.kind === 'native') return 'Standard field';
      if (destination?.kind === 'link') return 'Linked record';
      if (destination?.kind === 'dynamicAttribute') return 'Custom attribute';
      if (destination?.kind === 'definedCustomField') return 'Custom field';
      return 'Field';
    },
    writeChanged(write) {
      if (!write.hasCurrentValue) return true;
      // A link compares what was actually written, never the rendered text: only the proposed side
      // can name its record, so an unchanged link reads as "Connected record" against the resolved
      // name and a text check would report every one of them as changing.
      //
      // This leaves one case unhighlighted - same master, adoption mode flipped - because no current
      // adoption mode is recorded to compare against. That is not detectable, and inventing it would
      // inflate the "N of M fields change" count that operators judge impact by. The mode stays
      // visible in the value either way.
      if (write.isLink) return write.valueChanged;
      return write.current !== write.proposed;
    },
    writeSummary(item) {
      const writes = this.actionWrites(item);
      const changed = writes.filter((write) => this.writeChanged(write)).length;
      const noun = writes.length === 1 ? 'field' : 'fields';
      if (item.itemKind === 'action' && item.action === 'create') {
        return `${writes.length} ${noun} set on the new record`;
      }
      if (!changed) return `All ${writes.length} ${noun} already in sync`;
      return `${changed} of ${writes.length} ${noun} change`;
    },
    rowExpandable(item) {
      if (item?.redacted === true) return false;
      return Boolean(item?.refusal)
        || this.actionWrites(item).length > 0
        || this.fieldOutcomes(item).length > 0;
    },
    formatPlanValue(value) {
      if (value === undefined) return '(value omitted)';
      if (value === null) return 'null';
      if (typeof value === 'string') return value;
      try { return JSON.stringify(value); } catch (err) { return String(value); }
    },
    sourceValueState(item) {
      if (item?._sourceValueReadState === 'redacted'
        || item?.confirmBlockedCode === 'FACILITY_VIEW_REQUIRED') return 'redacted';
      if (item?._sourceValueReadState === 'unavailable') return 'unavailable';
      return 'available';
    },
    sourceValueLabel(item) {
      const state = this.sourceValueState(item);
      if (state === 'redacted') return 'Hidden: Facility access required';
      if (state === 'unavailable') return 'Not available for this previous run';
      return this.formatOperatorValue(item?.rowSnapshot?.sourceValue);
    },
    redactionLabel(item) {
      if (item?.confirmBlockedCode === 'FACILITY_VIEW_REQUIRED') return 'Facility details hidden';
      if (item?.confirmBlockedCode === 'TARGET_VIEW_REQUIRED') return 'Target details hidden';
      return 'Restricted details hidden';
    },
    fieldOutcomeLabel(outcome) {
      const safeOutcome = outcome && typeof outcome === 'object' ? outcome : {};
      const destinationIdentity = safeOutcome.destination || {};
      const destination = this.destinationLabel(destinationIdentity);
      const code = safeOutcome.reason?.code || safeOutcome.code;
      const reason = this.outcomeDetailMessage(code, safeOutcome.reason?.data, false)
        || this.reasonMessage(code);
      return `${destination}: ${reason}`;
    },
    // An outcome carries the text the row supplied, and for a conversion the destination's name, so
    // the operator reads which value failed rather than only that one did. Returns an empty string
    // for anything it cannot improve on, which keeps the code-to-message map as the fallback.
    //
    // `namesField` is false where the caller already prints the destination, so the sentence does
    // not say the field name twice.
    outcomeDetailMessage(code, data, namesField = true) {
      const value = this.nonEmptyString(data?.rawValue)
        ? this.formatOperatorValue(data.rawValue)
        : '';
      if (code === LINK_OUTCOME_CODES.unresolved && value) {
        return `No record named "${value}" was found. Nothing will change.`;
      }
      if (code === LINK_OUTCOME_CODES.ambiguous && value) {
        return this.validCandidateCount(data?.candidateCount) && Number(data.candidateCount) > 1
          ? `${Number(data.candidateCount)} Records are named "${value}". Nothing will change.`
          : `More than one record is named "${value}". Nothing will change.`;
      }
      if (code === CONVERSION_FAILED_CODE) {
        // The sentence leads with a word rather than the operator's text, which can start with any
        // character, so the first character is uppercase whatever was typed in the register.
        const field = this.nonEmptyString(data?.fieldLabel) && namesField
          ? this.safeFieldLabel(data.fieldLabel, 'this field')
          : 'this field';
        if (value) return `The value "${value}" is not valid for ${field}. Nothing will change.`;
        if (field !== 'this field') return `This value is not valid for ${field}. Nothing will change.`;
      }
      return '';
    },
    authorityLabel(item) {
      if (item.mapAuthorityDisposition === 'grant') return 'Authority candidate';
      if (item.mapAuthorityDisposition === 'none') return 'No authority';
      return 'Invalid';
    },
    authorityColor(item) {
      const disposition = String(item.mapAuthorityDisposition || '').toLowerCase();
      if (disposition === 'grant') return 'primary';
      if (disposition === 'none') return 'grey';
      return 'warning';
    },
    reasonLabel(item) {
      // AVA-602 N35: another register's link already keeps this Tag's fields in sync (one writer
      // per Tag), so this row connects without receiving the Tag's changes. Info, not an error.
      const syncElsewhere = 'Connected. Field sync for this Tag is owned by another register, so this row won’t receive the Tag’s changes.';
      if (item.itemKind === 'action') {
        if (item.reason?.code === 'syncOwnedByAnotherLink') return syncElsewhere;
        return this.fieldOutcomes(item).length ? 'Some fields need attention.' : 'Ready after approval.';
      }
      if (item.outcomeCode === 'alreadyLinked' && item.reason?.data?.syncOwnedByAnotherLink === true) {
        return syncElsewhere;
      }
      if (item.outcomeCode === 'ambiguous' && this.validCandidateCount(item.candidateCount)) {
        return `${Number(item.candidateCount)} possible records matched. Nothing will change.`;
      }
      const nestedCode = this.nestedOutcomeCode(item.reason);
      if (nestedCode) {
        // The row-level reason nests the code but not the value that failed; the matching field
        // outcome carries it, so the collapsed row can still name it.
        const fieldOutcome = this.fieldOutcomes(item)
          .find((outcome) => (outcome?.reason?.code || outcome?.code) === nestedCode);
        return this.outcomeDetailMessage(nestedCode, fieldOutcome?.reason?.data)
          || this.reasonMessage(nestedCode);
      }
      return this.reasonMessage(item.reason?.code || item.outcomeCode);
    },
    nestedOutcomeCode(reason) {
      const code = reason?.data?.code;
      return NESTED_ROW_OUTCOME_CODES.includes(code) ? code : '';
    },
    rowDecisionCode(item) {
      if (item?.itemKind === 'action') return item.action;
      return this.nestedOutcomeCode(item?.reason) || item?.outcomeCode;
    },
    reasonMessage(code) {
      const messages = {
        alreadyLinked: 'This record is already connected. Nothing will change.',
        alreadyInSync: 'Every mapped field already matches the register. Nothing will change.',
        ambiguous: 'More than one record matched. Nothing will change.',
        unresolved: 'No matching record was found. Nothing will change.',
        linkedTargetNotFound: 'No Tag has this name, and the row is still connected to its current Tag. Nothing will change. Change the target or unlink it.',
        sameNameInMemberFacility: 'A Facility in this Facility Group already has a Tag with this name. Nothing will change.',
        unresolvedMapping: 'A required field mapping is missing. Nothing will change.',
        outOfApplicability: 'This record is outside the relationship rules.',
        sourceValueMissing: 'The source value is empty. Nothing will change.',
        poiContextMissing: 'The row doesn’t name a Facility. Nothing will change.',
        sharedTargetConflict: 'Another record already uses this related record. Nothing will change.',
        sharedTargetPayloadConflict: 'Rows disagree on the values for the same related record. Nothing will change.',
        conversionFailed: 'The source value could not be converted for this field.',
        linkTargetUnresolved: 'No record matches the name in this field. Nothing will change.',
        linkTargetAmbiguous: 'More than one record matches the name in this field. Nothing will change.',
        linkTargetUnavailable: 'Records cannot be looked up for this field right now. Nothing will change.',
        targetSchemaDrift: 'The available fields changed after this preview was built. Use Preview run again.',
        targetPreviewRedacted: 'Details are hidden by your access level.',
        targetConcurrencyUnavailable: 'The related record could not be checked safely. Try a new preview.',
      };
      return messages[code] || 'Needs review before this row can continue.';
    },
    outcomeColor(code) {
      if (code === 'alreadyInSync') return 'success';
      if (['alreadyLinked', 'outOfApplicability'].includes(code)) return 'grey';
      if (['ambiguous', 'unresolved', 'unresolvedMapping', 'linkedTargetNotFound', 'sameNameInMemberFacility'].includes(code)) return 'warning';
      if (NESTED_ROW_OUTCOME_CODES.includes(code)) return 'warning';
      if (['sharedTargetConflict', 'sharedTargetPayloadConflict'].includes(code)) return 'error';
      return 'info';
    },
  },
};
</script>

<style lang="scss" scoped>
.relationship-run {
  /* Readable muted ink for light surfaces — the theme's text_secondary (#C8C8C8) fails contrast on white. */
  --run-muted-ink: #5c6b66;
  display: flex;
  height: min(860px, calc(100vh - 48px));
  max-height: calc(100vh - 48px);
  overflow: hidden;
  flex-direction: column;
  background: var(--v-bg2-base);
}
.relationship-run__body { display: flex; min-height: 0; flex: 1 1 auto; flex-direction: column; overflow-y: auto; }
/* min-height stays `auto` here: its content floor is what makes short screens fall back to body scroll. */
.run-content { display: flex; flex: 1 1 auto; flex-direction: column; }
.ds-dialog__title:focus-visible { outline: var(--ds-focus-ring-width) solid var(--ds-focus-ring-color); outline-offset: var(--ds-focus-ring-offset); }
.run-status-pill { display: inline-flex; overflow: hidden; align-items: stretch; flex: 0 0 auto; border: 1px solid currentColor; border-radius: 999px; color: var(--run-muted-ink); font-size: var(--ds-font-body-sm); font-weight: 600; background: var(--v-bg3-base); }
.run-status-pill__state,
.run-status-pill__timer { display: inline-flex; align-items: center; gap: 6px; padding: 6px 14px; white-space: nowrap; }
.run-status-pill .v-icon { color: inherit !important; }
.run-status-pill__timer { border-left: 1px solid currentColor; font-variant-numeric: tabular-nums; background: var(--v-bg2-base); }
.run-status-pill--primary, .run-status-pill--info { color: var(--v-primary-base); }
.run-status-pill--success { color: var(--v-success-base); }
.run-status-pill--warning { color: #a06d00; }
.run-status-pill--warning .run-status-pill__timer { background: rgba(251, 189, 28, 0.14); }
.run-status-pill--error { color: var(--v-error-base); }
.run-status-pill--grey { color: var(--run-muted-ink); }
.run-status-pill--urgent { color: var(--v-error-base); }
.run-status-pill--urgent .run-status-pill__timer { background: rgba(183, 28, 28, 0.08); }
.run-summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: var(--ds-space-sm); }
.run-summary-card { display: flex; flex-direction: column; padding: var(--ds-space-md); border: 1px solid var(--ds-border-subtle); border-radius: var(--ds-radius-md); background: var(--v-bg3-base); }
.run-summary-card span { color: var(--run-muted-ink); font-size: var(--ds-font-caption); }
.run-summary-card strong { font-size: var(--ds-font-title); }
.run-progress { padding: var(--ds-space-md); border: 1px solid var(--ds-border-default); border-radius: var(--ds-radius-md); background: var(--v-bg3-base); }
.confirmation-boundary {
  display: grid;
  align-items: center;
  flex: 0 0 auto;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: var(--ds-space-xs) var(--ds-space-md);
  padding: var(--ds-space-sm) var(--ds-space-lg);
  border-top: 1px solid var(--ds-border-default);
  background: var(--v-bg3-base);
}
.confirmation-boundary__message { display: flex; align-items: center; grid-column: 1 / -1; gap: var(--ds-space-xs); color: var(--v-warning-base); font-size: var(--ds-font-body-sm); }
.confirmation-boundary__checkbox { min-width: 0; margin: 0; padding: 0; }
.confirmation-boundary__hint { color: var(--run-muted-ink); font-size: var(--ds-font-caption); white-space: nowrap; }
.run-contract-blockers { display: flex; align-items: flex-start; gap: var(--ds-space-sm); padding: var(--ds-space-md); border: 1px solid rgba(210, 160, 0, 0.35); border-radius: var(--ds-radius-md); background: rgba(210, 160, 0, 0.08); }
.run-contract-blockers > div { min-width: 0; }
.run-contract-blockers ul { margin: var(--ds-space-xs) 0 0; padding-left: var(--ds-space-lg); color: var(--run-muted-ink); font-size: var(--ds-font-body-sm); }
.run-items-header { display: flex; align-items: center; justify-content: space-between; gap: var(--ds-space-md); }
.run-items-header__actions { display: flex; align-items: center; flex-wrap: wrap; justify-content: flex-end; gap: var(--ds-space-sm); }
.relationship-run__table { display: flex; width: 100%; min-height: 320px; flex: 1 1 320px; flex-direction: column; border: 1px solid var(--ds-border-subtle); border-radius: var(--ds-radius-md); overflow: hidden; background: var(--v-bg3-base); }
.relationship-run__table::v-deep .v-data-table__wrapper { min-height: 0; flex: 1 1 auto; overflow: auto !important; }
.relationship-run__table::v-deep table { min-width: 980px; table-layout: fixed; }
.relationship-run__table::v-deep thead th {
  position: sticky;
  z-index: 2;
  top: 0;
  color: var(--table-header-text) !important;
  background-color: var(--v-bg3-base) !important;
  background-image: linear-gradient(var(--table-header-tint), var(--table-header-tint)) !important;
  box-shadow: inset 0 -1px 0 var(--ds-border-default);
}
.relationship-run__table::v-deep tbody td { overflow-wrap: anywhere; vertical-align: middle; }
.relationship-run__table::v-deep tbody tr.run-row--flagged > td { background: rgba(251, 189, 28, 0.12); }
.relationship-run__table::v-deep tbody tr.run-row--flagged > td:first-child { box-shadow: inset 3px 0 0 #a06d00; }
.run-dec { display: inline-flex; align-items: center; gap: 6px; padding: 6px 11px 6px 8px; border-radius: 6px; color: var(--run-muted-ink); background: var(--v-bg2-base); font-size: var(--ds-font-caption); font-weight: 600; line-height: 1.2; white-space: nowrap; }
.run-dec .v-icon { flex: 0 0 auto; color: inherit !important; }
.run-dec { max-width: 100%; }
.run-dec__text { min-width: 0; overflow: hidden; text-overflow: ellipsis; }
.run-dec--primary, .run-dec--info { color: var(--v-primary-base); background: rgba(3, 98, 76, 0.09); }
.run-dec--success { color: #157a54; background: rgba(44, 194, 149, 0.13); }
.run-dec--warning { color: #a06d00; background: rgba(251, 189, 28, 0.14); }
.run-dec--error { color: var(--v-error-base); background: rgba(183, 28, 28, 0.08); }
.run-target-identity { display: flex; min-width: 150px; flex-direction: column; gap: 2px; }
.run-row-snapshot { display: flex; min-width: 150px; flex-direction: column; gap: 2px; }
.run-row-snapshot span { max-width: 240px; overflow: hidden; color: var(--run-muted-ink); text-overflow: ellipsis; white-space: nowrap; font-size: var(--ds-font-caption); }
.run-value-state--restricted { white-space: normal !important; }
.run-history-loading { display: flex; align-items: flex-start; gap: var(--ds-space-md); padding: var(--ds-space-lg); border: 1px solid var(--ds-border-default); border-radius: var(--ds-radius-md); background: var(--v-bg3-base); }
.run-history-loading h3 { margin: 0; font-size: var(--ds-font-title); }
.run-history-loading p { margin: var(--ds-space-xs) 0 0; color: var(--run-muted-ink); }
.run-target-identity small { color: var(--run-muted-ink); font-size: var(--ds-font-caption); }
.run-field-summary { display: flex; flex-direction: column; gap: 2px; color: var(--v-text-base); font-size: var(--ds-font-caption); }
.run-field-summary--none { color: var(--run-muted-ink); }
.run-field-summary__attention { color: #a06d00; font-weight: 600; }
.run-field-summary--refusal { min-width: 0; }
.run-field-summary--refusal span,
.run-field-summary--refusal small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.run-field-summary--refusal small { color: var(--run-muted-ink); font-size: var(--ds-font-caption); }
.run-result { display: inline-flex; align-items: center; gap: 5px; font-size: var(--ds-font-caption); font-weight: 600; }
.run-result .v-icon { color: inherit !important; }
.run-result--success { color: #157a54; }
.run-result--warning { color: #a06d00; }
.run-result--error { color: var(--v-error-base); }
.run-result--none { color: var(--run-muted-ink); font-weight: 400; }
.run-ledger__chev { transition: transform 0.2s ease-out; }
.run-ledger__chev--open { transform: rotate(180deg); }
.run-ledger__cell { padding: 0 !important; height: auto !important; background: var(--v-bg3-base); }
.run-ledger__block { border-left: 3px solid rgba(3, 98, 76, 0.18); padding: var(--ds-space-xs) 0; }
.run-ledger__row { display: grid; align-items: baseline; grid-template-columns: minmax(180px, 1.3fr) minmax(150px, 1fr) minmax(150px, 1fr); gap: var(--ds-space-sm); padding: 6px var(--ds-space-md) 6px 52px; border-top: 1px solid var(--ds-border-subtle); }
.run-ledger__row--head { border-top: 0; color: var(--run-muted-ink); font-size: 10px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; }
.run-ledger__prop { display: flex; min-width: 0; flex-direction: column; color: var(--v-text-base); font-size: var(--ds-font-caption); font-weight: 600; overflow-wrap: anywhere; }
.run-ledger__prop small { color: var(--run-muted-ink); font-size: 10px; font-weight: 400; line-height: 1.25; }
.run-ledger__val { min-width: 0; font-size: var(--ds-font-caption); overflow-wrap: anywhere; white-space: pre-wrap; }
.run-ledger__val small { color: var(--run-muted-ink); font-size: 10px; }
.run-ledger__val--muted { color: var(--run-muted-ink); }
.run-ledger__val--proposed { color: var(--v-primary-base); font-weight: 600; }
.run-ledger__issue { display: flex; align-items: center; gap: 6px; padding: 6px var(--ds-space-md) 6px 52px; border-top: 1px dashed var(--ds-border-subtle); color: #a06d00; font-size: var(--ds-font-caption); font-weight: 600; }
.run-ledger__restricted { padding: var(--ds-space-sm) var(--ds-space-md) var(--ds-space-sm) 52px; color: var(--run-muted-ink); font-size: var(--ds-font-caption); }
.run-recovery { display: flex; align-items: flex-start; gap: var(--ds-space-sm); padding: var(--ds-space-md); border: 1px solid var(--ds-border-default); border-radius: var(--ds-radius-md); background: var(--v-bg3-base); }
.run-recovery > div { display: flex; flex-direction: column; }
.run-recovery > div { min-width: 0; flex: 1; }
.run-recovery span { color: var(--run-muted-ink); font-size: var(--ds-font-body-sm); }
.run-recovery--warning { border-color: rgba(210, 160, 0, 0.35); }
.run-recovery--error { border-color: rgba(183, 28, 28, 0.35); }

@media (max-width: 720px) {
  .relationship-run__body { min-height: 0; padding: var(--ds-space-md) !important; }
  .ds-dialog__header { flex-wrap: wrap; padding: var(--ds-space-md); }
  .ds-dialog__header-text { flex-basis: calc(100% - 104px); }
  .ds-dialog__footer { flex-wrap: wrap; padding: var(--ds-space-md); }
  .run-scope-options .ds-selection-card { align-items: flex-start; flex-wrap: wrap; }
  .run-scope-options .ds-selection-card__content { min-width: 180px; }
  .run-scope-options .v-chip { margin-left: auto; }
  .run-status-pill__state,
  .run-status-pill__timer { padding: 5px 10px; }
  .run-ledger__row, .run-ledger__issue, .run-ledger__restricted { padding-left: var(--ds-space-md); }
  .run-summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .run-items-header { align-items: flex-start; flex-direction: column; }
  .run-items-header__actions { width: 100%; justify-content: space-between; }
  .run-failed-rows { flex-wrap: wrap; }
  .run-failed-rows .v-btn { width: 100%; }
}

.relationship-run ::v-deep .v-btn { min-height: 44px; }
.relationship-run ::v-deep .v-btn--icon { width: 44px; min-width: 44px; height: 44px; }

@media (prefers-reduced-motion: reduce) {
  .relationship-run *, .relationship-run *::before, .relationship-run *::after { transition-duration: 0.01ms !important; animation-duration: 0.01ms !important; }
}
</style>
